import {
  BoxGeometry,
  CapsuleGeometry,
  CircleGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
} from 'three';
import type { AvatarViewFactory } from '../core/types';
import type { VisualPreset } from './visual-presets';

export class PrimitiveAvatarFactory implements AvatarViewFactory<Group> {
  public constructor(private readonly preset: VisualPreset) {}

  public create(): Group {
    const avatar = new Group();
    avatar.name = 'primitive-avatar';

    const { palette, materials } = this.preset;
    const material = (color: number, roughness = materials.roughness): MeshStandardMaterial =>
      new MeshStandardMaterial({
        color,
        roughness,
        metalness: materials.metalness,
        flatShading: materials.flatShading,
      });
    const hoodie = material(palette.avatarHoodie);
    const skin = material(palette.avatarSkin, Math.min(1, materials.roughness + 0.08));
    const dark = material(palette.avatarDark);
    const accent = material(palette.avatarAccent, Math.max(0, materials.roughness - 0.07));
    const radialSegments = materials.geometrySegments;

    const torso = new Mesh(new CapsuleGeometry(0.36, 0.56, 4, radialSegments), hoodie);
    torso.position.y = 1.15;
    torso.name = 'torso';
    avatar.add(torso);

    const head = new Mesh(
      new SphereGeometry(0.36, radialSegments, Math.max(4, radialSegments - 2)),
      skin,
    );
    head.position.set(0, 1.93, -0.03);
    avatar.add(head);

    const hair = new Mesh(
      new SphereGeometry(
        0.37,
        radialSegments,
        Math.max(4, radialSegments - 2),
        0,
        Math.PI * 2,
        0,
        Math.PI / 2,
      ),
      dark,
    );
    hair.position.set(0, 2.04, -0.03);
    avatar.add(hair);

    const facePanel = new Mesh(new BoxGeometry(0.34, 0.19, 0.04), accent);
    facePanel.position.set(0, 1.9, -0.34);
    avatar.add(facePanel);

    for (const side of [-1, 1] as const) {
      const leg = new Mesh(new CapsuleGeometry(0.12, 0.46, 3, radialSegments), dark);
      leg.position.set(side * 0.19, 0.42, 0);
      leg.name = side === -1 ? 'left-leg' : 'right-leg';
      avatar.add(leg);

      const arm = new Mesh(new CapsuleGeometry(0.09, 0.42, 3, radialSegments), hoodie);
      arm.position.set(side * 0.46, 1.2, 0);
      arm.rotation.z = side * 0.18;
      arm.name = side === -1 ? 'left-arm' : 'right-arm';
      avatar.add(arm);
    }

    const shadow = new Mesh(
      new CircleGeometry(0.62, radialSegments * 2),
      new MeshStandardMaterial({
        color: palette.shadow,
        transparent: true,
        opacity: 0.35,
        roughness: materials.roughness,
      }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.015;
    shadow.name = 'shadow';
    avatar.add(shadow);

    return avatar;
  }
}
